import React, { useState } from "react";
import "./add-product.modal.scoped.scss";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { CheckIcon } from "@mantine/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { ProductsService } from "../../../../services/products.services";
import { ApplicationTypes } from "../../types/application-types";
import { urlRegex } from "../../../../statics/validators";
import { showPopupToastMsg, showToastMsg } from "../../../../helpers";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import { LangService } from "../../../../services/lang/lang.service";

const useStyles = makeStyles(() => ({
  datePackerInputRoot: {
    backgroundColor: "var(--light)",
    height: "100%",
    paddingTop: 0,
    margin: "0 !important",
  },
  datePackerInputAdornment: {
    margin: "0 !important",
  },
}));

const AddProductModal = (props: any) => {
  const handleClose = (value: any) => props.onClose(value);
  const { t } = useTranslation();
  const classes = useStyles();
  const productServices = new ProductsService();
  const langService: LangService = new LangService();
  const locale = langService.getCurrentLang();
  const dateToday = new Date();
  dateToday.setDate(dateToday.getDate() + 1);

  moment.locale(locale);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ link: "" });
  const [license_validity, setLicense_validity]: any = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    vendor: "",
    usage: "",
    version: "",
    version_comment: "",
    version_details: "",
    type: "",
    environment: "",
    link: "",
    changes: "",
    license_type: "",
    license_validity: "",
  });

  const isFormValid = Object.values(formData).every(
    (value) => value.trim() !== "",
  );

  const validateLink = (value: string) =>
    value
      ? urlRegex.test(value)
        ? ""
        : t("INVALID_LINK_FORMAT")
      : t("LINK_REQUIRED");
  // handle input changes
  const handleChange = (e: any, isDate?: Boolean) => {
    if (isDate) {
      setLicense_validity(e);
      setFormData((prev) => ({
        ...prev,
        license_validity: moment(e).format("YYYY-MM-DD"),
      }));
    } else {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === "link") {
        setErrors((prev) => ({ ...prev, link: validateLink(value) }));
      }
    }
  };

  const submit = async () => {
    setSaving(true);
    const response = await productServices.add(formData);
    if (response && response.message === "SUCCESS") {
      showToastMsg("success", t("PRODUCTS_PAGE.PRODUCT_ADDED_SUCCESSFULLY"));
      handleClose(true);
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
    setSaving(false);
  };

  return (
    <Modal
      show={props.show}
      onHide={handleClose.bind(null, false)}
      centered
      dialogClassName={
        props.dialogClassName ? props.dialogClassName : "modal-lg"
      }
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("PRODUCTS_PAGE.ADD_PRODUCT")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box className="add__product_form">
          <Box className="grid-2 mb-3">
            <TextField
              fullWidth
              label={t("PRODUCTS_PAGE.PRODUCT_NAME")}
              onChange={handleChange}
              value={formData.name}
              name="name"
              variant={"filled"}
              inputProps={{
                maxLength: 30,
              }}
            />
            <TextField
              fullWidth
              label={t("PRODUCTS_PAGE.VENDOR_NAME")}
              onChange={handleChange}
              value={formData.vendor}
              name="vendor"
              variant={"filled"}
              inputProps={{
                maxLength: 30,
              }}
            />
          </Box>
          <Box className="grid-1 mb-3">
            <TextField
              className="text-area-multiline"
              fullWidth
              label={t("PRODUCTS_PAGE.PRODUCT_USAGE")}
              onChange={handleChange}
              value={formData.usage}
              name="usage"
              variant={"filled"}
              multiline
              rows={3}
              inputProps={{
                maxLength: 500,
              }}
            />
          </Box>
          <Box className="grid-2 mb-3">
            <TextField
              fullWidth
              label={t("PRODUCTS_PAGE.PRODUCT_VERSION")}
              onChange={handleChange}
              value={formData.version}
              name="version"
              variant={"filled"}
              inputProps={{
                maxLength: 10,
              }}
            />
            <TextField
              fullWidth
              label={t("PRODUCTS_PAGE.VERSION_COMMENT")}
              onChange={handleChange}
              value={formData.version_comment}
              name="version_comment"
              variant={"filled"}
              inputProps={{
                maxLength: 150,
              }}
            />
          </Box>
          <Box className="grid-1 mb-3">
            <TextField
              className="text-area-multiline"
              fullWidth
              label={t("PRODUCTS_PAGE.VERSION_DETAILS")}
              onChange={handleChange}
              value={formData.version_details}
              name="version_details"
              variant={"filled"}
              multiline
              rows={3}
              inputProps={{
                maxLength: 500,
              }}
            />
          </Box>
          <Box className="grid-2 mb-3">
            <FormControl className="select-input select-grey w-100">
              <InputLabel id="type">
                {t("PRODUCTS_PAGE.APPLICATION_TYPE")}
              </InputLabel>
              <Select
                labelId="type"
                name="type"
                onChange={(e: any) => handleChange(e)}
                IconComponent={ExpandMoreIcon}
                value={formData.type || ""}
              >
                {ApplicationTypes.map((value: any, index: number) => {
                  return (
                    <MenuItem key={index} value={value.id}>
                      {t(value.name)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={t("PRODUCTS_PAGE.ENVIRONMENT")}
              onChange={handleChange}
              value={formData.environment}
              name="environment"
              variant={"filled"}
              inputProps={{
                maxLength: 50,
              }}
            />
          </Box>
          <Box className="grid-1 mb-3">
            <TextField
              fullWidth
              label={t("LINK")}
              onChange={handleChange}
              value={formData.link}
              name="link"
              variant={"filled"}
              error={Boolean(errors.link)}
              helperText={errors.link}
            />
          </Box>
          <Box className="grid-2 mb-3">
            <TextField
              fullWidth
              label={t("PRODUCTS_PAGE.LICENSE_TYPE")}
              onChange={handleChange}
              value={formData.license_type}
              name="license_type"
              variant={"filled"}
              inputProps={{
                maxLength: 30,
              }}
            />
            <MuiPickersUtilsProvider
              utils={MomentUtils}
              libInstance={moment}
              locale={locale}
            >
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="DD/MM/YYYY"
                autoOk
                value={license_validity}
                onChange={(e: any) => handleChange(e, true)}
                name="license_validity"
                className="date-picker-standard w-100"
                label={t("SELECT_DUE_DATE")}
                minDate={dateToday}
                keyboardIcon={<EventOutlinedIcon />}
                inputVariant="filled"
                InputProps={{
                  classes: { root: classes.datePackerInputRoot },
                }}
                InputLabelProps={{
                  style: { zIndex: 1 },
                }}
                InputAdornmentProps={{
                  classes: { root: classes.datePackerInputAdornment },
                }}
              />
            </MuiPickersUtilsProvider>
          </Box>

          <Box className="grid-1 mb-3">
            <TextField
              className="text-area-multiline"
              fullWidth
              label={t("PRODUCTS_PAGE.INDIVIDUAL_CHANGES")}
              onChange={handleChange}
              value={formData.changes}
              name="changes"
              variant={"filled"}
              multiline
              rows={3}
              inputProps={{
                maxLength: 500,
              }}
            />
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outlined"
          color="primary"
          className="btn-secondary-theme"
          onClick={handleClose.bind(null, false)}
        >
          {t("cancel")}
        </Button>
        <Button
          disabled={!isFormValid || saving || Boolean(errors.link)}
          onClick={submit}
          className="btn-primary-theme"
          variant="contained"
          color="primary"
        >
          <CheckIcon /> {t("SUBMIT")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddProductModal;
